import React from 'react';
import { Lightbulb, AlertTriangle, CheckCircle, Info, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Insight {
  id?: string;
  text: string;
  type: 'positive' | 'warning' | 'info' | 'success';
  priority?: 'high' | 'medium' | 'low';
  actionable?: boolean;
}

interface InsightsListProps {
  insights: Insight[];
  title?: string;
  maxItems?: number;
  showIcons?: boolean;
  variant?: 'default' | 'compact' | 'minimal';
  className?: string;
  onInsightClick?: (insight: Insight) => void;
}

export const InsightsList: React.FC<InsightsListProps> = ({
  insights,
  title = "Insights",
  maxItems = 3,
  showIcons = true,
  variant = 'default',
  className,
  onInsightClick
}) => {
  const displayedInsights = insights.slice(0, maxItems);

  const getInsightIcon = (type: string): LucideIcon => {
    switch (type) {
      case 'positive':
      case 'success':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'info':
        return Info;
      default:
        return Lightbulb;
    }
  };

  const getInsightStyles = (insight: Insight) => {
    const baseStyles = "rounded-lg border transition-all duration-200";
    
    if (variant === 'minimal') {
      return cn(baseStyles, "p-2 border-l-4 border-r-0 border-t-0 border-b-0 bg-transparent", {
        'border-l-green-500 text-green-700': insight.type === 'positive' || insight.type === 'success',
        'border-l-orange-500 text-orange-700': insight.type === 'warning',
        'border-l-blue-500 text-blue-700': insight.type === 'info',
      });
    }

    if (variant === 'compact') {
      return cn(baseStyles, "p-2 border", {
        'bg-green-50/50 border-green-200/50 text-green-800': insight.type === 'positive' || insight.type === 'success',
        'bg-orange-50/50 border-orange-200/50 text-orange-800': insight.type === 'warning',
        'bg-blue-50/50 border-blue-200/50 text-blue-800': insight.type === 'info',
        'bg-muted/50 border-border text-muted-foreground': !['positive', 'success', 'warning', 'info'].includes(insight.type),
      });
    }

    return cn(baseStyles, "p-3 border", {
      'bg-green-50 border-green-200 text-green-800': insight.type === 'positive' || insight.type === 'success',
      'bg-orange-50 border-orange-200 text-orange-800': insight.type === 'warning',
      'bg-blue-50 border-blue-200 text-blue-800': insight.type === 'info',
      'bg-white/60 border-white/20': !['positive', 'success', 'warning', 'info'].includes(insight.type),
    });
  };

  const getPriorityIndicator = (priority?: string) => {
    switch (priority) {
      case 'high':
        return 'h-2 w-2 rounded-full bg-red-500';
      case 'medium':
        return 'h-2 w-2 rounded-full bg-orange-500';
      case 'low':
        return 'h-2 w-2 rounded-full bg-blue-500';
      default:
        return null;
    }
  };

  if (!insights.length) {
    return (
      <div className={cn("text-center py-6 text-muted-foreground", className)}>
        <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No insights available</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {title && (
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Lightbulb className="h-4 w-4" />
          {title}
        </div>
      )}
      
      <div className="space-y-2">
        {displayedInsights.map((insight, index) => {
          const Icon = getInsightIcon(insight.type);
          const priorityClass = getPriorityIndicator(insight.priority);
          
          return (
            <div
              key={insight.id || index}
              className={cn(
                getInsightStyles(insight),
                onInsightClick && "cursor-pointer hover:shadow-sm",
                insight.actionable && "border-dashed"
              )}
              onClick={() => onInsightClick?.(insight)}
            >
              <div className="flex items-start gap-3">
                {showIcons && (
                  <div className="flex items-center gap-2 mt-0.5 flex-shrink-0">
                    <Icon className="h-4 w-4" />
                    {priorityClass && <div className={priorityClass} />}
                  </div>
                )}
                <div className="flex-1">
                  <p className={cn(
                    variant === 'compact' ? "text-xs" : "text-sm",
                    insight.actionable && "font-medium"
                  )}>
                    {insight.text}
                  </p>
                  {insight.actionable && (
                    <div className="text-xs text-current/70 mt-1">
                      Click to take action
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {insights.length > maxItems && (
        <div className="text-center pt-2">
          <span className="text-xs text-muted-foreground">
            Showing {maxItems} of {insights.length} insights
          </span>
        </div>
      )}
    </div>
  );
};